import { ModelRow, StyleUtil } from "@mcleod/core";
import { Layout, LayoutProps } from "@mcleod/components";
import { TimelineCardLayout } from "./TimelineCardLayout";

export class TimelineCard {
    private static readonly UNFOCUSED_ZINDEX = 4;
    private static readonly FOCUSED_ZINDEX = 7;
    private _layout: TimelineCardLayout;
    private _collapsedHeight: string|number;
    private _collapsedWidth: string|number;
    private _selected: boolean;
    private _parentRow: ModelRow;
    private _overlap: boolean = false;
    private _expanded: boolean = true;

    constructor(layoutName: string, layoutProps: Partial<LayoutProps>, data: ModelRow, parentRow?: ModelRow) {
        const props: Partial<LayoutProps> = { scrollX: false, scrollY: false, ...layoutProps };
        this.layout = Layout.getLayout(layoutName, props) as TimelineCardLayout;
        this.layout.addLayoutLoadListener(() => {
            this.data = data;
            this.layout.style.position = "absolute";
            this.setupMouseHoverListeners();
            this.parentRow = parentRow;
        });
    }

    get layout(): TimelineCardLayout {
        return this._layout;
    }

    private set layout(value: TimelineCardLayout) {
        this._layout = value;
    }

    set data(value: ModelRow) {
        this.layout.data = value;
        this.layout.displayData(value, null, 0);
    }

    get data(): ModelRow {
        return this.layout.data;
    }

    get collapsedHeight(): string|number {
        return this._collapsedHeight;
    }

    set collapsedHeight(value: string|number) {
        this._collapsedHeight = value;
        this.layout.height = this.collapsedHeight;
    }

    get collapsedWidth(): string|number {
        return this._collapsedWidth;
    }

    set collapsedWidth(value: string|number) {
        this._collapsedWidth = value;
        this.layout.width = this.collapsedWidth;
    }

    get startDate(): Date {
        return this.layout.startDate;
    }

    get endDate(): Date {
        return this.layout.endDate;
    }

    get selected(): boolean {
        return this._selected;
    }

    set selected(value: boolean) {
        if (value) {
            this.expand();
        } else {
            this.collapse();
        }
        this._selected = value;
    }

    get parentRow(): ModelRow {
        return this._parentRow;
    }

    set parentRow(value: ModelRow) {
        this._parentRow = value;
    }

    get overlap(): boolean {
        return this._overlap;
    }

    set overlap(value: boolean) {
        this._overlap = value;
    }

    get expanded(): boolean {
        return this._expanded;
    }

    set expanded(value: boolean) {
        this._expanded = value;
    }

    place(position: number, top: number = 0) {
        this.layout.style.top = StyleUtil.numberAsRems(top);
        this.layout.style.left = StyleUtil.numberAsRems(position);
    }

    public setupMouseHoverListeners() {
        this.layout.element.addEventListener("mouseover", () => this.layout.zIndex = TimelineCard.FOCUSED_ZINDEX);
        this.layout.element.addEventListener("mouseleave", () => {
            if (this.selected)
                return;
            this.layout.zIndex = TimelineCard.UNFOCUSED_ZINDEX
        });
    }

    expand = () => {
        this.layout.height = this.layout.expandedHeight;
        this.layout.width = undefined;
        this.layout.zIndex = TimelineCard.FOCUSED_ZINDEX;
    }

    collapse = () => {
        this.layout.height = this.collapsedHeight;
        this.layout.width = this.collapsedWidth;
        this.layout.zIndex = TimelineCard.UNFOCUSED_ZINDEX;
        this.layout.element.style.boxShadow = "none";
    }
}
